/**
 * dashboardStats.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for all dashboard-facing metrics.
 *
 * All dashboard components MUST derive their numbers from this module.
 * This eliminates divergence between HeroSection, ProgressOverview,
 * QuickInsights, StatsDrawer, LevelLadder, and SkillMasteryPanel.
 */

import { ModuleData } from '../content';
import { AppState, UserStats } from '../app/types';
import type { QuestionState, QuestionAttempt } from '../mastery';
import { GroupId } from '../config/systemConfig';
import { getModuleMasteryFromFamilies } from '../mastery';
import { LEVEL_THRESHOLDS, getLevelForXP, getProgressToNextLevel } from '../config/constants';

// ─── XP / Level helpers (internal) ────────────────────────────────────────────
// Level thresholds are now imported from config/constants.ts to ensure a single source of truth.

export function getXPToNextLevel(xp: number): number {
  const level = getLevelForXP(xp);
  if (level >= LEVEL_THRESHOLDS.length) return 0;
  const nextLevelXP = LEVEL_THRESHOLDS[level];
  return Math.max(0, nextLevelXP - xp);
}

// ─── Attempt-based stats ──────────────────────────────────────────────────────

export function computeAccuracy(attempts: QuestionAttempt[]): number {
  if (!attempts || attempts.length === 0) return 0;
  return attempts.filter(a => a.isCorrect).length / attempts.length;
}
export function computeRecentAccuracy(attempts: QuestionAttempt[], last = 20): number {
  if (!attempts || attempts.length === 0) return 0;
  const recent = attempts.slice(-last);
  return computeAccuracy(recent);
}
export function computeCurrentStreak(attempts: QuestionAttempt[]): number {
  if (!attempts || attempts.length === 0) return 0;
  let streak = 0;
  // Sort by timestamp descending (newest first) for streak calculation
  const sorted = [...attempts].sort((a, b) => b.timestamp - a.timestamp);
  for (const attempt of sorted) {
    if (attempt.isCorrect) streak++;else break;
  }
  return streak;
}
export function computeLongestStreak(attempts: QuestionAttempt[]): number {
  if (!attempts || attempts.length === 0) return 0;
  let longest = 0;
  let current = 0;
  // Sort by timestamp ascending (oldest first) for chronological processing
  const sorted = [...attempts].sort((a, b) => a.timestamp - b.timestamp);
  for (const a of sorted) {
    if (a.isCorrect) {
      current++;
      longest = Math.max(longest, current);
    } else current = 0;
  }
  return longest;
}

/**
 * Collect attempts filtered by group.
 * Question state keys use format "questionId_gX" where X is the group number.
 * Only includes attempts from keys matching the specified group.
 */
export function collectAttemptsForGroup(questionStates: Record<string, QuestionState> | undefined, group: GroupId): QuestionAttempt[] {
  if (!questionStates) return [];
  const groupSuffix = `_g${group}`;
  const all: QuestionAttempt[] = [];
  for (const [key, state] of Object.entries(questionStates)) {
    // Only include entries that match the group suffix
    if (key.endsWith(groupSuffix)) {
      if (state.history && Array.isArray(state.history)) {
        all.push(...state.history);
      }
    }
  }
  // Sort by timestamp ascending (oldest first)
  return all.sort((a, b) => a.timestamp - b.timestamp);
}

// ─── Dashboard Stats Interface ────────────────────────────────────────────────
export interface DashboardStats extends UserStats {
  levelProgress: number;
  xpToNextLevel: number;
  currentStreak: number;
  longestStreak: number;
  accuracy: number;
  recentAccuracy: number;
  totalQuestions: number;
  correctAnswers: number;
  overallMastery: number; // 0-100
  learningTime: number; // Estimated in minutes
}

// ─── Main Aggregation Function ────────────────────────────────────────────────
export function getDashboardStats(state: AppState, modules: ModuleData[], selectedGroup: GroupId): DashboardStats {
  const {
    xp,
    level,
    name
  } = state.stats;

  // 1. XP & Level Stats
  const progressData = getProgressToNextLevel(xp);
  const levelProgress = progressData.percent / 100;
  const xpToNextLevel = getXPToNextLevel(xp);

  // 2. Attempt Stats — GROUP-SCOPED (fixed: was global before Rev 17)
  const groupAttempts = collectAttemptsForGroup(state.questionStates, selectedGroup);
  const totalQuestions = groupAttempts.length;
  const correctAnswers = groupAttempts.filter(a => a.isCorrect).length;
  const accuracy = computeAccuracy(groupAttempts);
  const recentAccuracy = computeRecentAccuracy(groupAttempts);
  const currentStreak = computeCurrentStreak(groupAttempts);
  const longestStreak = computeLongestStreak(groupAttempts);

  // 3. Overall Mastery (Group Scoped)
  let overallMastery = 0;
  if (modules.length > 0) {
    const practicedModules = modules.filter(m => {
      const mastery = getModuleMasteryFromFamilies(state.familyMastery || {}, m.id, selectedGroup);
      return mastery > 0;
    });
    if (practicedModules.length > 0) {
      const totalMastery = practicedModules.reduce((sum, m) => {
        return sum + getModuleMasteryFromFamilies(state.familyMastery || {}, m.id, selectedGroup);
      }, 0);
      overallMastery = Math.round(totalMastery / practicedModules.length);
    }
  }

  // 4. Learning Time (Estimated)
  // Estimate: 1 minute per question on average (simple heuristic)
  // In a real app, we would track session duration.
  const learningTime = Math.round(totalQuestions * 1.5); // 1.5 mins per question estimate

  return {
    name,
    xp,
    level,
    levelProgress,
    xpToNextLevel,
    currentStreak,
    longestStreak,
    accuracy,
    recentAccuracy,
    totalQuestions,
    correctAnswers,
    overallMastery,
    learningTime
  };
}